import { useUser } from "@/hooks/useUserProvider";
import {
  ModalHeader,
  ResponsiveDialog,
  ResponsiveDialogContent,
} from "../ui/responsive-dialog";
import { useEffect, useState } from "react";
import { Skeleton } from "../ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { ChangePasswordModal } from "./ChangePasswordModal";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Button } from "../ui/button";
import { Integrations } from "./Integrations";
import { UserAccount } from "./UserAccount";
import {
  AlertTriangleIcon,
  CreditCardIcon,
  PuzzleIcon,
  SettingsIcon,
  UserIcon,
  StickyNoteIcon,
  CheckSquareIcon,
} from "lucide-react";
import { Free } from "./Billing/Free";
import { Premium } from "./Billing/Premium";
import { AccountDangerZone } from "./UserAccount/AccountDangerZone";
import { InternalNote } from "./InternalNote";
import { InternalTodo } from "./InternalTodo";

export const UserModal = ({
  changePasswordToken,
  open: isUserModalOpen,
  onOpenChange: setUserModalOpen,
}: {
  changePasswordToken?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const {
    user,
    syncUser,
    isLoading,
    isError,
    error,
    accessToken,
    setAuthenticationModalOpen,
  } = useUser();

  const [isChangePasswordModalOpen, setChangePasswordModalOpen] =
    useState(!!changePasswordToken);

  const checkShouldOpen = () => {
    if (!isUserModalOpen) {
      return;
    }

    if (!accessToken) {
      setUserModalOpen(false);
      setAuthenticationModalOpen(true);
      return;
    }

    syncUser();
  };

  useEffect(() => {
    checkShouldOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserModalOpen, accessToken]);

  return (
    <ResponsiveDialog open={isUserModalOpen} onOpenChange={setUserModalOpen}>
      <ResponsiveDialogContent
        className="flex h-[700px] max-h-[calc(100vh-100px)] max-w-[1000px] flex-col overflow-hidden border-0 p-4 focus-visible:ring-0 sm:rounded-xl"
        draggable={false}
        disableClose
        drawerClassName="h-[92dvh] bg-card text-foreground"
      >
        <Tabs
          defaultValue="account"
          className="flex min-h-0 flex-1 flex-col gap-4"
        >
          <ModalHeader
            icon={SettingsIcon}
            title="Settings"
            description="Manage your account, integrations, and workspace."
          />
          <div className="flex min-h-0 flex-1 flex-col gap-4 lg:grid lg:grid-cols-[260px_minmax(0,1fr)]">
            <div className="lg:bg-card shrink-0 lg:overflow-hidden lg:rounded-xl lg:border">
              <TabsList variant="sidebar" className="bg-transparent p-2">
                <TabsTrigger
                  className="data-[state=active]:bg-secondary h-10 shrink-0 justify-start rounded-lg px-3 text-sm data-[state=active]:text-white lg:w-full"
                  value="account"
                >
                  <UserIcon className="me-2 size-4" />
                  Account
                </TabsTrigger>
                <TabsTrigger
                  className="data-[state=active]:bg-secondary h-10 shrink-0 justify-start rounded-lg px-3 text-sm data-[state=active]:text-white lg:w-full"
                  value="integrations"
                >
                  <PuzzleIcon className="me-2 size-4" />
                  Integrations
                </TabsTrigger>
                <TabsTrigger
                  className="data-[state=active]:bg-secondary h-10 shrink-0 justify-start rounded-lg px-3 text-sm data-[state=active]:text-white lg:w-full"
                  value="internal-notes"
                >
                  <StickyNoteIcon className="me-2 size-4" />
                  Internal Notes
                </TabsTrigger>
                <TabsTrigger
                  className="data-[state=active]:bg-secondary h-10 shrink-0 justify-start rounded-lg px-3 text-sm data-[state=active]:text-white lg:w-full"
                  value="internal-todos"
                >
                  <CheckSquareIcon className="me-2 size-4" />
                  Internal Todos
                </TabsTrigger>
                <TabsTrigger
                  className="data-[state=active]:bg-secondary h-10 shrink-0 justify-start rounded-lg px-3 text-sm data-[state=active]:text-white lg:w-full"
                  value="billing"
                >
                  <CreditCardIcon className="me-2 size-4" />
                  Billing
                </TabsTrigger>
                <TabsTrigger
                  className="data-[state=active]:bg-secondary h-10 shrink-0 justify-start rounded-lg px-3 text-sm data-[state=active]:text-white lg:w-full"
                  value="danger"
                >
                  <AlertTriangleIcon className="me-2 size-4" />
                  Danger Zone
                </TabsTrigger>
              </TabsList>
            </div>
            <div className="lg:bg-card min-h-0 flex-1 overflow-y-auto lg:rounded-xl lg:border lg:p-6">
              {isLoading ? (
                <div className="flex flex-col gap-4">
                  <Skeleton className="h-6 w-40 rounded-xl" />
                  <Skeleton className="h-28 rounded-xl" />
                  <Skeleton className="h-28 rounded-xl" />
                </div>
              ) : isError ? (
                <Alert variant="destructive">
                  <AlertTitle>Failed to load user profile</AlertTitle>
                  <AlertDescription className="flex flex-col gap-4">
                    {error?.message ||
                      "An error occurred while loading the user profile"}
                    <Button
                      onClick={syncUser}
                      variant="default"
                      className="w-auto"
                    >
                      Retry
                    </Button>
                  </AlertDescription>
                </Alert>
              ) : !user ? (
                <Alert variant="default">
                  <AlertTitle>You are not logged in</AlertTitle>
                  <AlertDescription>
                    Please login to view your profile
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="flex flex-col">
                  <TabsContent
                    value="account"
                    className="mt-0 flex flex-col gap-4"
                  >
                    <UserAccount user={user} />
                  </TabsContent>
                  <TabsContent
                    value="integrations"
                    className="mt-0 flex flex-col"
                  >
                    <Integrations />
                  </TabsContent>
                  <TabsContent
                    value="internal-notes"
                    className="mt-0 flex flex-col"
                  >
                    <InternalNote />
                  </TabsContent>
                  <TabsContent
                    value="internal-todos"
                    className="mt-0 flex flex-col"
                  >
                    <InternalTodo />
                  </TabsContent>
                  <TabsContent
                    value="billing"
                    className="mt-0 flex flex-col gap-4"
                  >
                    {user?.subscription ? <Premium /> : <Free />}
                  </TabsContent>
                  <TabsContent
                    value="danger"
                    className="mt-0 flex flex-col gap-4"
                  >
                    <AccountDangerZone />
                  </TabsContent>
                </div>
              )}
            </div>
          </div>
        </Tabs>
        {isChangePasswordModalOpen && !!changePasswordToken ? (
          <ChangePasswordModal
            changePasswordToken={changePasswordToken}
            onOpenChange={setChangePasswordModalOpen}
          />
        ) : null}
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
};
